import React, { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { ContentCard, LoadingSkeleton } from "@narmi/design_system";
import ApiHttp from "byzantine/src/ApiHttp";

import ConnectionItem from "./ConnectionItem";
import RevokeConfirmationDialog from "./RevokeConfirmationDialog";
import type { DataAccessConsent, DataAccessConsentListResponse } from "./types";
import sharedStyles from "../SecurityShared.module.scss";
import styles from "./ThirdPartyDataAccess.module.scss";


const ThirdPartyDataAccess = () => {
  const { l10n } = useLocalization();
  const [connections, setConnections] = useState<DataAccessConsent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionToRevoke, setConnectionToRevoke] = useState<DataAccessConsent | null>(null);

  const loadConnections = () => {
    setIsLoading(true);
    ApiHttp.fetch("data_access_management")
      .then((response: DataAccessConsentListResponse) => {
        setConnections(response.results);
        setError(null);
      })
      .catch(() => {
        setError(l10n.getString("error-loading-connections"));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    loadConnections()
  }, []);

  const handleRevoke = (connection: DataAccessConsent) => {
    setConnectionToRevoke(connection);
  };

  const handleConfirmRevoke = () => {
    if (!connectionToRevoke) return;
    ApiHttp.fetch(
      "data_access_management/revoke",
      { method: "POST" },
      {
        id: connectionToRevoke.id,
        data_partner: connectionToRevoke.data_partner,
        name: connectionToRevoke.name,
        display_name: connectionToRevoke.display_name,
        logo_url: connectionToRevoke.logo_url,
        connected_at: connectionToRevoke.connected_at,
      }
    )
      .then(loadConnections)
      .catch(() => {
        setError(l10n.getString("error-revoking-connection"));
      })
      .finally(() => {
        setConnectionToRevoke(null);
      });
  };

  return (
    <div className={sharedStyles["security-card"]}>
      <ContentCard>
        <div className={sharedStyles["alerts-title-container"]}>
          <h3 className="semibold">
            {l10n.getString("heading-third-party-data-access")}
          </h3>
        </div>
        <p className={styles.description}>
          {l10n.getString("description-third-party-data-access")}
        </p>
        {isLoading && (
          <LoadingSkeleton content="paragraph" lines={3} isLoading />
        )}
        {!isLoading && error && (
          <p className={styles["empty-state"]}>{error}</p>
        )}
        {!isLoading && !error &&
          (connections.length > 0 ? (
            <div className={styles["connections-card"]}>
              {connections.map((connection) => (
                <ConnectionItem
                  key={connection.id}
                  connection={connection}
                  onRevoke={handleRevoke}
                />
              ))}
            </div>
          ) : (
            <p className={styles["empty-state"]}>
              {l10n.getString("empty-no-active-connections")}
            </p>
          ))}
      </ContentCard>
      <RevokeConfirmationDialog
        connection={connectionToRevoke}
        isOpen={connectionToRevoke !== null}
        onClose={() => setConnectionToRevoke(null)}
        onConfirm={handleConfirmRevoke}
      />
    </div>
  );
};

export default ThirdPartyDataAccess;
